var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { isNumber, isString, mapValues, omitBy, isNil } from 'lodash-es';
import { LogAction, LogLevel, getErrorRecord, SafeJson, toFlatPropertyMap, } from '../core';
const levelMap = {
    [LogLevel.INFO]: 'info',
    [LogLevel.SUCCESS]: 'success',
    [LogLevel.WARNING]: 'warn',
    [LogLevel.ERROR]: 'error',
};
function logLevelToSlardarLevel(level = LogLevel.INFO) {
    return levelMap[level];
}
/**
 * 将 meta 根据类型转换成
 * - 指标 metrics：可以被度量的值，也就是数值
 * - 维度 categories：分类，维度，用来做筛选，分组
 * https://bytedance.feishu.cn/docs/doccnBXKovvHEZ2tG2dBdf2xoOh#
 * @param meta
 * @returns
 */
function metaToMetricsCategories(meta) {
    const metrics = {};
    const categories = {};
    for (const k in meta) {
        const val = meta[k];
        if (isNumber(val)) {
            metrics[k] = val;
        }
        else {
            categories[k] = isString(val) ? val : SafeJson.stringify(val);
        }
    }
    return {
        metrics,
        categories,
    };
}
/**
 * Record<string, unknown> => Record<string, string | number>
 */
function normalizeExtra(record) {
    const result = {};
    for (const k in record) {
        const val = record[k];
        if (isNumber(val) || isString(val)) {
            result[k] = val;
        }
        else {
            result[k] = SafeJson.stringify(val);
        }
    }
    return result;
}
export class SlardarReportClient {
    constructor(slardarInstance) {
        // 业务项目里可能有多个 slardar 版本，多个版本的类型不兼容，constructor 里约束版本会存在问题 => 放开。
        this.slardarInstance = slardarInstance;
        if (!this.slardarInstance) {
            console.warn('expected slardarInstance but get undefined/null');
        }
    }
    send(options) {
        var _a, _b, _c, _d;
        if (!((_a = options.action) === null || _a === void 0 ? void 0 : _a.includes(LogAction.PERSIST))) {
            // 非持久化日志，不上报
            return;
        }
        const { level, message, action, eventName, meta, error } = options, rest = __rest(options, ["level", "message", "action", "eventName", "meta", "error"]);
        // Slardar API：https://bytedance.feishu.cn/wiki/wikcngEnmUajGyuNL9TksufU4ub
        const resolveMeta = (inputs) => toFlatPropertyMap(Object.assign(Object.assign(Object.assign({}, rest), inputs), { error: getErrorRecord(error), level: logLevelToSlardarLevel(level) }), {
            maxDepth: 4,
        });
        if (level === LogLevel.ERROR && (meta === null || meta === void 0 ? void 0 : meta.reportJsError) === true) {
            const _e = meta || {}, { reportJsError, reactInfo } = _e, restMeta = __rest(_e, ["reportJsError", "reactInfo"]);
            const resolvedMeta = resolveMeta(Object.assign(Object.assign({}, restMeta), { message,
                eventName }));
            // 上报 JS 异常
            (_b = this.slardarInstance) === null || _b === void 0 ? void 0 : _b.call(this, 'captureException', error, omitBy(mapValues(resolvedMeta, (v) => {
                return isString(v) ? v : SafeJson.stringify(v);
            }), isNil), reactInfo);
        }
        else if (eventName) {
            const resolvedMeta = resolveMeta(Object.assign({}, meta));
            const { metrics, categories } = metaToMetricsCategories(resolvedMeta);
            // 上报独立的事件
            (_c = this.slardarInstance) === null || _c === void 0 ? void 0 : _c.call(this, 'sendEvent', {
                name: eventName,
                metrics,
                categories,
            });
        }
        else if (message) {
            const resolvedMeta = resolveMeta(Object.assign({}, meta));
            // 上报日志
            (_d = this.slardarInstance) === null || _d === void 0 ? void 0 : _d.call(this, 'sendLog', {
                // level: logLevelToSlardarLevel(level),
                content: message,
                // slardar 内部会对 extra 处理分类，number 类型的字段放入 metrics，其他放入 categories
                extra: normalizeExtra(resolvedMeta),
            });
        }
    }
}
export * from './forwardToTea';
